<script setup lang="ts">
/* =============================================================
   COMPARE BERTHS
   =============================================================
   Up to four berths side by side for one ship and one stay, on
   exactly the five things §4 of the brief lists: price,
   availability, vessel compatibility, amenities and distance.
   The brief calls this "one of the key improvements" -- today an
   operator collects these from several sources by hand.

   Each row marks the best value (cheapest, closest) so the
   answer is visible without reading every cell. Amenity rows are
   the ones the ship needs first, then the rest the berths offer.

   Distance is from the place searched for when there is one, and
   from the first berth otherwise.
   ============================================================= */

import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import Badge from '@/components/ui/Badge.vue'
import Icon from '@/components/Icon.vue'
import SearchControls from '@/components/search/SearchControls.vue'
import { to } from '@/config/routes'
import { getBerth } from '@/lib/berths'
import { listCities, searchBerths } from '@/lib/search'
import { listVessels } from '@/lib/vessels'
import { useAmenities, AMENITY_ORDER } from '@/lib/amenities'
import { formatEur } from '@/lib/bookingLabels'
import { bookingModeText, formatDistance, resultStatus } from '@/lib/searchLabels'
import { errorMessage } from '@/lib/errors'
import { useSearchContext } from '@/lib/useSearchContext'
import { useAuth } from '@/lib/auth/useAuth'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import type { AmenityCode, BerthResult, Vessel } from '@/lib/types'

useDocumentTitle('Compare berths')

const route = useRoute()
const { context, amenities, hasStay, update } = useSearchContext()
const { user } = useAuth()
const companyId = () => user.value?.companyId ?? 0
const { amenityLabel } = useAmenities()

const ids = computed(() =>
  String(route.query.ids ?? '')
    .split(',')
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0)
    .slice(0, 4),
)

const vessels = ref<Vessel[]>([])
const berths = ref<BerthResult[]>([])
const loading = ref(true)
const error = ref('')

const vessel = computed(() => vessels.value.find((v) => v.id === context.value.vessel) ?? null)

let origin: { lat: number; lng: number } | null = null

async function findOrigin(): Promise<{ lat: number; lng: number } | null> {
  if (context.value.city) {
    const city = (await listCities()).find((c) => c.city === context.value.city)
    if (city) return { lat: city.latitude, lng: city.longitude }
  }
  const first = ids.value[0] ? await getBerth(ids.value[0]) : null
  return first ? { lat: first.latitude, lng: first.longitude } : null
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    if (vessels.value.length === 0) vessels.value = await listVessels(companyId())
    origin ??= await findOrigin()
    if (!origin) {
      berths.value = []
      return
    }
    const rows = await searchBerths({
      lat: origin.lat,
      lng: origin.lng,
      radiusKm: 100,
      vessel: vessel.value,
      amenities: amenities.value,
      arrival: hasStay.value ? context.value.from : null,
      departure: hasStay.value ? context.value.to : null,
    })
    // In the order they were picked, not by distance.
    berths.value = ids.value.map((id) => rows.find((r) => r.id === id)).filter((r): r is BerthResult => !!r)
  } catch (e) {
    error.value = errorMessage(e, 'Could not compare these berths.')
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch([ids, vessel, () => context.value.from, () => context.value.to, amenities], load, { deep: true })

/* --- the best in each row ------------------------------------------------ */

function bestBy(values: Array<number | null>): number | null {
  const real = values.filter((v): v is number => v !== null)
  return real.length > 1 ? Math.min(...real) : null
}

const cheapest = computed(() =>
  bestBy(berths.value.map((b) => (resultStatus(b, hasStay.value).bookable ? b.price_eur : null))),
)
const closest = computed(() => bestBy(berths.value.map((b) => b.distance_km)))

/** Needed amenities first, then whatever else any of these berths offers. */
const amenityRows = computed<Array<{ code: AmenityCode; needed: boolean }>>(() => {
  const needed = new Set(amenities.value)
  const offered = new Set(berths.value.flatMap((b) => b.amenities))
  return AMENITY_ORDER.filter((c) => needed.has(c) || offered.has(c)).map((code) => ({
    code,
    needed: needed.has(code),
  }))
})

const back = computed(() => to.search(context.value))
</script>

<template>
  <PageHeader
    title="Compare berths"
    description="Side by side for your ship and stay: price, availability, fit, amenities and distance."
  >
    <template #actions>
      <Button variant="ghost" icon="chevronRight" :to="back" class="back-link">Search</Button>
    </template>
  </PageHeader>

  <Panel class="controls">
    <SearchControls :context="context" :vessels="vessels" @update="update" />
  </Panel>

  <Alert v-if="error" tone="danger" class="page-alert">{{ error }}</Alert>

  <p v-if="loading && berths.length === 0" class="page-status">Loading…</p>

  <Panel v-else-if="berths.length < 2">
    <p class="panel-status">Pick at least two berths in the search to compare them.</p>
  </Panel>

  <Panel v-else flush>
    <div class="scroll">
      <table class="compare">
        <thead>
          <tr>
            <th scope="col" class="rowHead"><span class="sr-only">Berth</span></th>
            <th v-for="b in berths" :key="b.id" scope="col" class="berthHead">
              <RouterLink :to="to.berthProfile(b.id, context)" class="berthName">{{ b.name }}</RouterLink>
              <span class="berthWhere">{{ b.company_name }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row" class="rowHead">Price</th>
            <td v-for="b in berths" :key="b.id" :class="{ best: hasStay && b.price_eur === cheapest }">
              <template v-if="hasStay && b.price_eur !== null">
                <span class="big">{{ formatEur(b.price_eur) }}</span>
                <Badge v-if="b.price_eur === cheapest" tone="success" class="tag">Cheapest</Badge>
              </template>
              <span v-else class="muted">{{ hasStay ? 'No price' : 'Pick dates' }}</span>
            </td>
          </tr>
          <tr>
            <th scope="row" class="rowHead">Availability</th>
            <td v-for="b in berths" :key="b.id">
              <span v-if="!hasStay" class="muted">Pick dates</span>
              <span v-else-if="b.is_free" class="yes"><Icon name="check" :size="16" /> Free</span>
              <span v-else class="no"><Icon name="close" :size="16" /> Taken</span>
            </td>
          </tr>
          <tr>
            <th scope="row" class="rowHead">Fits {{ vessel ? vessel.name : 'your ship' }}</th>
            <td v-for="b in berths" :key="b.id">
              <span v-if="b.fits" class="yes"><Icon name="check" :size="16" /> Yes</span>
              <span v-else class="no"><Icon name="close" :size="16" /> {{ b.misfit }}</span>
            </td>
          </tr>
          <tr>
            <th scope="row" class="rowHead">Limits</th>
            <td v-for="b in berths" :key="b.id" class="small">
              {{ b.max_length_m ? `${b.max_length_m} m long` : 'Length not stated' }}<template v-if="b.max_width_m">, {{ b.max_width_m }} m wide</template><template v-if="b.max_draft_m">, {{ b.max_draft_m }} m draft</template>
            </td>
          </tr>
          <tr v-for="a in amenityRows" :key="a.code">
            <th scope="row" class="rowHead">
              {{ amenityLabel(a.code) }}
              <span v-if="a.needed" class="needed">needed</span>
            </th>
            <td v-for="b in berths" :key="b.id">
              <span v-if="b.amenities.includes(a.code)" class="yes"><Icon name="check" :size="16" /></span>
              <span v-else :class="a.needed ? 'no' : 'muted'"><Icon name="close" :size="16" /></span>
            </td>
          </tr>
          <tr>
            <th scope="row" class="rowHead">Distance{{ context.city ? ` from ${context.city}` : '' }}</th>
            <td v-for="b in berths" :key="b.id" :class="{ best: b.distance_km === closest }">
              {{ formatDistance(b.distance_km) }}
              <Badge v-if="b.distance_km === closest" tone="accent" class="tag">Closest</Badge>
            </td>
          </tr>
          <tr>
            <th scope="row" class="rowHead">Booking</th>
            <td v-for="b in berths" :key="b.id" class="small">{{ bookingModeText(b.booking_mode, b.response_hours) }}</td>
          </tr>
          <tr>
            <th scope="row" class="rowHead"><span class="sr-only">Actions</span></th>
            <td v-for="b in berths" :key="b.id">
              <Button
                size="sm"
                :to="resultStatus(b, hasStay).bookable ? to.book(b.id, context) : undefined"
                :disabled="!resultStatus(b, hasStay).bookable"
              >
                Book
              </Button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Panel>
</template>

<style scoped>
.controls {
  margin-bottom: var(--space-md);
}

.scroll {
  overflow-x: auto;
}

.compare {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}

.compare th,
.compare td {
  padding: var(--space-xs) var(--space-md);
  text-align: start;
  vertical-align: middle;
  border-bottom: var(--border-width) solid var(--color-border);
}

.compare tr:last-child th,
.compare tr:last-child td {
  border-bottom: 0;
}

/* The row labels stay put while a phone scrolls the berths sideways. */
.rowHead {
  position: sticky;
  left: 0;
  z-index: 1;
  width: 11rem;
  min-width: 9rem;
  font-weight: var(--weight-medium);
  color: var(--color-text-muted);
  background-color: var(--color-surface);
}

.berthHead {
  min-width: 11rem;
  vertical-align: bottom;
  background-color: var(--color-surface-sunken);
}

thead .rowHead {
  background-color: var(--color-surface-sunken);
}

.berthName {
  display: block;
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
  text-decoration: none;
}

.berthName:hover {
  color: var(--color-accent);
  text-decoration: underline;
}

.berthWhere {
  display: block;
  margin-top: 2px;
  font-size: var(--text-xs);
  font-weight: var(--weight-regular, 400);
  color: var(--color-text-muted);
}

.big {
  font-size: var(--text-md);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
  color: var(--color-heading);
}

.best {
  background-color: color-mix(in srgb, var(--color-success) 7%, transparent);
}

.tag {
  margin-inline-start: var(--space-2xs);
}

.yes,
.no,
.muted {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
}

.yes {
  color: var(--color-success);
  font-weight: var(--weight-medium);
}

.no {
  color: var(--color-danger);
}

.muted {
  color: var(--color-text-faint);
}

.small {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.needed {
  margin-inline-start: var(--space-3xs);
  padding: 0 var(--space-3xs);
  font-size: var(--text-xs);
  color: var(--color-accent-hover);
  background-color: var(--color-accent-soft);
  border-radius: var(--radius-full);
}
</style>
